import { Component, inject } from "@angular/core";
import { Router } from "@angular/router";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { LocationSelectorComponent } from "../../../../Common/Components/LocationSelector/LocationSelectorComponent";
import { RichTextEditorComponent } from "../../../../Common/Components/RichTextEditor/RichTextEditorComponent";
import { LocationAddress } from "../../../../Common/Components/LocationSelector/Type/LocationSelectorTypes";
import { WarehouseEffects } from "../../State/WarehouseEffects";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { CreateWarehouseRequest } from "../../Dto/CreateWarehouseRequest";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-warehouse-create",
  standalone: true,
  imports: [ReactiveFormsModule, AppButtonComponent, AppCardComponent, LocationSelectorComponent, RichTextEditorComponent],
  templateUrl: "./WarehouseCreatePage.html",
  styleUrl: "./WarehouseCreatePage.scss",
})
export class WarehouseCreatePage {
  private readonly router = inject(Router);
  private readonly warehouseEffects = inject(WarehouseEffects);
  private readonly toastEffects = inject(ToastEffects);
  private readonly fb = inject(FormBuilder);

  warehouseForm: FormGroup;
  isSaving = false;

  constructor() {
    this.warehouseForm = this.fb.group({
      name: ["", [Validators.required, Validators.minLength(2)]],
      note: [""],
      address: this.fb.group({
        stateProvinceRegion: ["", [Validators.required]],
        countyDistrict: ["", [Validators.required]],
        subdistrictMunicipality: ["", [Validators.required]],
        communeVillage: ["", [Validators.required]],
        line: [""],
        zip: [""],
        longitude: [0],
        latitude: [0],
      }),
    });
  }

  async saveWarehouse(): Promise<void> {
    if (this.warehouseForm.invalid) {
      this.warehouseForm.markAllAsTouched();
      this.toastEffects.show("Please fill in all required fields.", "error");
      return;
    }

    this.isSaving = true;

    const formData = this.warehouseForm.value;
    const payload: CreateWarehouseRequest = {
      name: formData.name,
      address: {
        stateProvinceRegion: formData.address.stateProvinceRegion,
        countyDistrict: formData.address.countyDistrict,
        subdistrictMunicipality: formData.address.subdistrictMunicipality,
        communeVillage: formData.address.communeVillage,
        line: formData.address.line || "",
        zip: formData.address.zip || "",
        longitude: 0,
        latitude: 0,
      },
    };

    const noteValue = (() => {
      if (typeof formData.note === "string") {
        return formData.note.trim();
      }
      return "";
    })();
    if (noteValue) {
      payload.note = noteValue;
    }

    try {
      await this.warehouseEffects.createWarehouse(payload);
      this.toastEffects.show("Warehouse created successfully.", "success");
      await this.router.navigate(["/warehouses"]);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to create warehouse."), "error");
    } finally {
      this.isSaving = false;
    }
  }

  goBack(): void {
    this.router.navigate(["/warehouses"]).catch(() => undefined);
  }

  onAddressChange(value: LocationAddress): void {
    this.warehouseForm.patchValue({
      address: {
        stateProvinceRegion: value.stateProvinceRegion,
        countyDistrict: value.countyDistrict,
        subdistrictMunicipality: value.subdistrictMunicipality,
        communeVillage: value.communeVillage,
        line: value.line,
        zip: value.zip,
      },
    });
  }
}
